import { memo, useState, useCallback } from "react";
import ChoixRenderer from "../ChoixRenderer";
import { getChoixValue } from "./QCMEleve";
import ChoixImageEleve from "./ChoixImageEleve";
import ChoixZoomButton from "./ChoixZoomButton";
import { useQuizTheme, answerTheme } from "./quizTheme";
import { etatTuile, MarqueCorrection, TUILE_NEUTRE } from "./corrige";

/**
 * Composant interactif QCM multi-réponses côté élève — checkboxes.
 * L'élève peut cocher plusieurs réponses. La réponse envoyée est un JSON array trié.
 *
 * Design adaptatif via useQuizTheme() : 'clair' (carte blanche, mockup) pendant
 * la passation, 'sombre' (défaut) ailleurs. Aucune modif de logique.
 *
 * @param {Array} choix - Les choix ["texte", {type:'image', config:{imageUrl}}, ...]
 * @param {string} selectedValue - La réponse déjà sélectionnée (JSON array stringify ou null)
 * @param {Function} onAnswer - Callback (JSON string) quand l'élève modifie sa sélection
 * @param {Function} onComplete - Callback (bool) pour signaler que la réponse est complète
 */
const QCMMultiEleve = memo(function QCMMultiEleve({ choix, selectedValue, onAnswer, onComplete, corrige = null }) {
    const clair = useQuizTheme() === 'clair';
    // Correction SUR PLACE (lot 1, 2026-09-21) : mêmes trois états que le QCM
    // simple, et ici ils cohabitent sur la même liste (une cochée juste, une
    // cochée pour rien, une oubliée). Absente : le rendu d'avant, au bit près.
    const t = answerTheme(clair);

    // Restaurer la sélection depuis selectedValue (JSON array stringify)
    const [selectedSet, setSelectedSet] = useState(() => {
        if (!selectedValue) return new Set();
        try {
            const parsed = JSON.parse(selectedValue);
            if (Array.isArray(parsed)) return new Set(parsed);
        } catch {}
        return new Set();
    });

    const hasElement = choix.some(c => typeof c === 'object' && c !== null);

    // Toggle un choix dans le Set
    const handleToggle = useCallback((value) => {
        const next = new Set(selectedSet);
        if (next.has(value)) next.delete(value);
        else next.add(value);
        setSelectedSet(next);

        // Notifier le parent
        if (next.size === 0) {
            onComplete(false);
        } else {
            onAnswer(JSON.stringify([...next].sort()));
            onComplete(true);
        }
    }, [selectedSet, onAnswer, onComplete]);

    // Guard — après les hooks
    if (!choix || choix.length === 0) return null;

    return (
        <>
            {/* Instructions : pas en correction, il n'y a plus rien à cocher */}
            {corrige ? null : (
                <p className={`text-xs sm:text-sm font-bold flex items-center gap-2 ${clair ? 'text-slate-500' : 'text-slate-300'}`}>
                    <span>☑️</span> Coche les bonnes réponses puis clique sur suivant !
                </p>
            )}

            {/* Réponses — texte pleine largeur (1 col) ; images en tuiles 2×2 (Duolingo) */}
            <div className={`grid gap-3 mt-3 sm:mt-4 ${hasElement ? 'grid-cols-2' : 'grid-cols-1'}`}>
                {choix.map((item, index) => {
                    const value = getChoixValue(item);
                    const isSelected = selectedSet.has(value);
                    const isObjectChoix = typeof item === 'object' && item !== null;
                    const isImage = isObjectChoix && item?.type === 'image';
                    const imageUrl = isImage ? (item.config?.imageUrl || '') : null;

                    const etat = etatTuile(corrige, value);
                    const stateClass = corrige
                        ? (etat ? t[etat] : (clair ? TUILE_NEUTRE.clair : TUILE_NEUTRE.sombre))
                        : (clair
                            ? (isSelected
                                ? 'border-[#3B82F6] border-b-[5px] border-b-[#2563EB] bg-blue-50 active:translate-y-[3px] active:border-b-2'
                                : 'border-slate-200 border-b-[5px] border-b-slate-300 bg-white hover:border-blue-300 active:translate-y-[3px] active:border-b-2')
                            : (isSelected
                                ? 'border-[#29B6E8] bg-[#29B6E8]/20 shadow-lg shadow-[#29B6E8]/20'
                                : 'border-white/10 bg-white/5 hover:border-[#29B6E8]/60 hover:bg-white/10'));
                    const textClass = corrige
                        ? (etat ? 'text-inherit' : (clair ? 'text-slate-800' : 'text-slate-100'))
                        : (clair
                            ? (isSelected ? 'text-[#2563EB]' : 'text-slate-800')
                            : 'text-slate-100');

                    return (
                        <div key={index} className="relative flex">
                        <label
                            className={`relative flex w-full items-center justify-center text-center p-3 sm:p-4 border-2 rounded-2xl transition-all duration-200 ${corrige ? 'cursor-default' : 'cursor-pointer hover:scale-[1.01]'} ${isImage ? 'min-h-[7rem]' : ''} ${stateClass}`}
                        >
                            {etat ? <MarqueCorrection etat={etat} libelleAttendu="à cocher aussi" /> : null}
                            {isImage ? (
                                /* Choix image : tuile « Duolingo » — image contenue + placeholder si indisponible (tuile cliquable) */
                                <div className="pointer-events-none w-full flex items-center justify-center">
                                    <ChoixImageEleve
                                        src={imageUrl || item.config?.imageData || ''}
                                        alt={`Réponse ${index + 1}`}
                                    />
                                </div>
                            ) : isObjectChoix ? (
                                /* Élément visuel (droite / expression / geogebra) : pointer-events-none →
                                   la tuile sélectionne, le zoom interne de l'élément ne se déclenche plus (loupe dédiée) */
                                <div className="pointer-events-none w-full flex items-center justify-center">
                                    <ChoixRenderer
                                        choix={item}
                                        light={clair}
                                        className={`text-lg sm:text-xl font-semibold ${textClass}`}
                                    />
                                </div>
                            ) : (
                                /* Texte (string) via ChoixRenderer (rendu KaTeX des $...$) */
                                <ChoixRenderer
                                    choix={item}
                                    light={clair}
                                    className={`text-lg sm:text-xl font-semibold ${textClass}`}
                                />
                            )}

                            {/* Input réel masqué (le label entier est la cible cliquable) */}
                            <input
                                type="checkbox"
                                checked={isSelected}
                                disabled={Boolean(corrige)}
                                onChange={() => handleToggle(value)}
                                className="sr-only"
                            />
                        </label>

                        {/* Loupe HORS du <label> → un clic dessus ne peut pas déclencher la sélection */}
                        {isObjectChoix && <ChoixZoomButton choix={item} light={clair} />}
                        </div>
                    );
                })}
            </div>
        </>
    );
});

export default QCMMultiEleve;
